/**
 * HANA diagnostic skin quiz.
 *
 * The backend (quiz-api) is the single source of truth for questions, option ids,
 * branching and scoring. This element fetches the definition at runtime, collects
 * answer ids and submits them; it renders whatever comes back. Never hardcode
 * question text, option ids, order or branching here.
 *
 * Contract: docs/superpowers/quiz-api-contract.md
 * Design spec: docs/superpowers/specs/2026-07-05-skin-quiz-ui-design.md
 */

(() => {
  'use strict';

  const COPY = {
    startFailedHint: 'The quiz is taking a break right now.',
    retry: 'Try again',
    back: 'Back',
    next: 'Next',
    continue: 'Continue',
    followUp: 'Quick follow-up',
    pickToContinue: 'Pick an option to continue.',
    almostThere: 'Almost there',
    pickUpTo: (max, count) => `Pick up to ${max} · ${count} selected`,
    scoring: 'Reading your answers…',
    submitFailed: "That didn't go through. Your answers are safe.",
    rateLimited: 'Lots of people are taking the quiz right now. Give us a moment.',
    retryIn: (s) => `Try again in ${s}s`,
    emailHeadline: 'Where should we send your results?',
    emailSub: 'Your routine lands in your inbox so you can shop it later, plus a heads-up when your retake reminder is due.',
    firstNamePlaceholder: 'First name',
    emailPlaceholder: 'you@example.com',
    consent: 'Opt in for results, skincare news and offers from HANA.',
    seeResults: 'See my results',
    skip: 'Skip, just show my result',
    emailInvalid: "That email doesn't look right. Check it and try again.",
    emailPutFailed: "That didn't go through. Give it another try.",
    resultEyebrow: 'Your skin reading',
    skinTypeLabel: 'Skin type',
    concernsLabel: 'Skin concerns',
    yourRoutine: 'Your routine',
    optionalTag: 'Optional',
    addToCart: 'Add to cart',
    inCart: '✓ In cart',
    addAllToCart: 'Add all to cart',
    viewCart: 'View cart',
    addToCartFailed: "That didn't add to cart. Give it another try.",
    emailMeRoutine: 'Email me this routine',
    emailSaved: 'Done. Your routine is on its way.',
    retakeHeadline: 'Retake in two weeks',
    retakeWhy: 'Two weeks of a calm baseline and your answers will point somewhere much more specific.',
    retakeRemind: 'Remind me by email',
    retakeReminderSet: "We'll email you when it's time.",
    consultAsk: "If you're comfortable, Harry will reach out by email with a couple of follow-up questions and build your routine personally.",
    consultYes: 'Yes, reach out',
    consultNo: 'No thanks',
    consultEmailLead: 'Where should Harry reach you?',
    consultConfirmed: 'Expect an email from Harry in a day or two.',
    consultDeclined: 'No worries. Your result stays right here on this page.',
    retakeLink: 'Retake the quiz',
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (key === 'text') node.textContent = value;
      else if (key === 'class') node.className = value;
      else if (key === 'dataset') Object.assign(node.dataset, value);
      else if (key.startsWith('on') && typeof value === 'function') node.addEventListener(key.slice(2), value);
      else if (value === true) node.setAttribute(key, '');
      else node.setAttribute(key, value);
    }
    for (const child of [].concat(children)) {
      if (child === null || child === undefined) continue;
      node.append(child);
    }
    return node;
  }

  function slotLabel(slot) {
    const words = String(slot).replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  function formatPrice(price) {
    const amount = Number(price);
    return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : String(price);
  }

  class SkinQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.api = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.browseUrl = this.dataset.browseUrl || '/collections/all';
      this.browseLabel = this.dataset.browseLabel || 'Browse best sellers';

      this.intro = this.querySelector('[data-quiz-intro]');
      this.stage = this.querySelector('[data-quiz-stage]');
      this.progress = this.querySelector('.quiz__progress');
      this.progressFill = this.querySelector('.quiz__progress-fill');
      this.progressLabel = this.querySelector('.quiz__progress-label');
      this.startButton = this.querySelector('[data-quiz-start]');
      this.introHint = this.querySelector('[data-quiz-intro-hint]');

      this.definition = null;
      this.initState();

      this.startButton.addEventListener('click', () => this.start());
      this.loadDefinition();
    }

    initState() {
      this.answers = {};
      this.stepIndex = 0;
      this.email = null;
      this.firstName = null;
      this.marketingConsent = false;
      // True until a PUT succeeds: "no email attached yet", so late forms know to show.
      this.emailSkipped = true;
      // Variant ids already added this session, so "Add all" never double-adds.
      this.cartAdded = new Set();
      this.rateLimitTimer = null;
      this.resetSubmission();
    }

    resetSubmission() {
      this.submission = null;
      this.submissionPromise = null;
      this.submittedFingerprint = null;
    }

    track(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(`quiz:${name}`, { bubbles: true, detail }));
    }

    /* ---------- definition ---------- */

    async fetchJson(path, options = {}) {
      // Content-Type only when there is a body: a bare GET stays a "simple"
      // CORS request and skips the preflight round-trip.
      const headers = options.body ? { 'Content-Type': 'application/json' } : {};
      return fetch(this.api + path, { credentials: 'omit', ...options, headers });
    }

    async loadDefinition() {
      this.introHint.hidden = false;
      try {
        this.definition = await this.tryDefinition();
      } catch (error) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        try {
          this.definition = await this.tryDefinition();
        } catch (retryError) {
          this.introHint.textContent = COPY.startFailedHint;
          this.intro.append(this.fallbackActions());
          return;
        }
      }
      this.introHint.hidden = true;
      this.startButton.disabled = false;
    }

    async tryDefinition() {
      const response = await this.fetchJson('/v1/definition');
      if (!response.ok) throw new Error(`definition ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data.questions) || data.questions.length === 0) throw new Error('empty definition');
      return data;
    }

    fallbackActions() {
      return el('div', { class: 'quiz__actions v-stack gap-2' }, [
        el('button', {
          type: 'button', class: 'button button--secondary', text: COPY.retry,
          onclick: (event) => {
            event.target.closest('.quiz__actions').remove();
            this.introHint.textContent = 'Getting things ready…';
            this.loadDefinition();
          },
        }),
        el('a', { class: 'quiz__text-link', href: this.browseUrl, text: this.browseLabel }),
      ]);
    }

    /* ---------- branching ---------- */

    conditionMet(showIf, answers = this.answers) {
      const controlling = answers[showIf.question];
      if (controlling === undefined) return false;
      const selected = Array.isArray(controlling) ? controlling : [controlling];
      return showIf.has_any.some((id) => selected.includes(id));
    }

    visibleQuestions() {
      return this.definition.questions.filter((q) => !q.show_if || this.conditionMet(q.show_if));
    }

    /** Drop answers to questions whose show_if condition is no longer met. */
    pruneOrphanAnswers() {
      let changed = true;
      while (changed) {
        changed = false;
        for (const q of this.definition.questions) {
          if (q.show_if && this.answers[q.id] !== undefined && !this.conditionMet(q.show_if)) {
            delete this.answers[q.id];
            changed = true;
          }
        }
      }
    }

    fingerprint() {
      return JSON.stringify(this.visibleQuestions().map((q) => [q.id, this.answers[q.id]]));
    }

    /* ---------- screens ---------- */

    swapStage(node) {
      this.intro.hidden = true;
      this.stage.hidden = false;
      this.stage.replaceChildren(node);
      const heading = this.stage.querySelector('[data-quiz-focus]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: false });
      }
      window.scrollTo({ top: 0 });
    }

    showIntro() {
      this.progress.hidden = true;
      this.progressFill.style.width = '0%';
      this.stage.hidden = true;
      this.stage.replaceChildren();
      this.intro.hidden = false;
    }

    setProgress(answered, total) {
      this.progress.hidden = false;
      const ratio = total === 0 ? 0 : Math.min(answered / total, 1);
      this.progressFill.style.width = `${ratio * 100}%`;
      this.progressLabel.hidden = !(ratio >= 0.75 && ratio < 1);
    }

    start() {
      this.track('start', { quiz_version: this.definition.quiz_version });
      this.stepIndex = 0;
      this.showQuestion();
    }

    showQuestion() {
      const visible = this.visibleQuestions();
      if (this.stepIndex >= visible.length) {
        this.goToEmail();
        return;
      }
      const question = visible[this.stepIndex];
      this.setProgress(this.stepIndex, visible.length);
      this.track('question_view', { id: question.id });

      const isMulti = question.type === 'multi';
      const body = el('div', { class: 'quiz__question v-stack gap-4' }, [
        question.show_if ? el('span', { class: 'quiz__pill', text: COPY.followUp }) : null,
        el('h2', { class: 'quiz__prompt h2', 'data-quiz-focus': true, text: question.prompt }),
        question.subline ? el('p', { class: 'quiz__helper quiz__subline', text: question.subline }) : null,
        isMulti && question.max_select
          ? el('p', { class: 'quiz__helper', 'data-quiz-counter': true, 'aria-live': 'polite', text: this.counterText(question) })
          : null,
        this.optionList(question),
        el('div', { class: 'quiz__actions quiz__actions--split' }, [
          el('button', {
            type: 'button', class: 'button button--outline quiz__back',
            text: COPY.back,
            onclick: () => this.goBack(),
          }),
          el('button', {
            type: 'button', class: 'button quiz__advance', 'data-quiz-next': true,
            text: isMulti ? COPY.continue : COPY.next,
            onclick: () => {
              if (!this.hasAnswer(question)) {
                this.showSelectWarning();
                return;
              }
              this.goNext();
            },
          }),
        ]),
      ]);
      this.swapStage(body);
    }

    hasAnswer(question) {
      const answer = this.answers[question.id];
      return Array.isArray(answer) ? answer.length > 0 : answer !== undefined;
    }

    counterText(question) {
      const count = (this.answers[question.id] || []).length;
      return COPY.pickUpTo(question.max_select, count);
    }

    optionList(question) {
      const isMulti = question.type === 'multi';
      const role = isMulti ? null : 'radiogroup';
      const regular = question.options.filter((o) => !o.exclusive);
      const exclusive = question.options.filter((o) => o.exclusive);

      const list = el('div', {
        class: 'quiz__options v-stack gap-2',
        role,
        'aria-label': question.prompt,
        onkeydown: role ? (event) => this.radioKeydown(event, question) : null,
      });
      for (const option of regular) list.append(this.optionCard(question, option));
      if (exclusive.length) {
        list.append(el('hr', { class: 'quiz__divider', 'aria-hidden': 'true' }));
        for (const option of exclusive) list.append(this.optionCard(question, option));
      }
      return list;
    }

    optionCard(question, option) {
      const isMulti = question.type === 'multi';
      const selected = this.isSelected(question, option);
      return el('button', {
        type: 'button',
        class: 'quiz__option',
        role: isMulti ? 'checkbox' : 'radio',
        'aria-checked': selected ? 'true' : 'false',
        dataset: { optionId: option.id },
        onclick: () => this.pickOption(question, option),
      }, [
        // Empty media slot, image-ready: populated if the definition ever carries option imagery.
        el('span', { class: 'quiz__option-media', 'aria-hidden': 'true', hidden: true }),
        el('span', { class: 'quiz__option-copy', text: option.copy }),
        el('span', { class: 'quiz__option-mark', 'aria-hidden': 'true' }),
      ]);
    }

    isSelected(question, option) {
      const answer = this.answers[question.id];
      return Array.isArray(answer) ? answer.includes(option.id) : answer === option.id;
    }

    pickOption(question, option) {
      if (question.type === 'single') {
        this.answers[question.id] = option.id;
      } else {
        let selection = [...(this.answers[question.id] || [])];
        if (selection.includes(option.id)) {
          selection = selection.filter((id) => id !== option.id);
        } else if (option.exclusive) {
          selection = [option.id];
        } else {
          selection = selection.filter((id) => {
            const other = question.options.find((o) => o.id === id);
            return other && !other.exclusive;
          });
          if (question.max_select && selection.length >= question.max_select) {
            this.pulseCounter();
            return;
          }
          selection.push(option.id);
        }
        if (selection.length === 0) delete this.answers[question.id];
        else this.answers[question.id] = selection;
      }
      this.pruneOrphanAnswers();
      this.track('question_answer', { id: question.id, answer: this.answers[question.id] });
      this.refreshQuestionState(question);
    }

    refreshQuestionState(question) {
      const answer = this.answers[question.id];
      const selection = Array.isArray(answer) ? answer : answer !== undefined ? [answer] : [];
      const atCap = question.type === 'multi' && question.max_select && selection.length >= question.max_select;
      for (const card of this.stage.querySelectorAll('.quiz__option')) {
        const isOn = selection.includes(card.dataset.optionId);
        card.setAttribute('aria-checked', isOn ? 'true' : 'false');
        card.classList.toggle('quiz__option--muted', Boolean(atCap && !isOn));
      }
      const counter = this.stage.querySelector('[data-quiz-counter]');
      if (counter) counter.textContent = this.counterText(question);
      if (this.hasAnswer(question)) {
        const warning = this.stage.querySelector('[data-quiz-warning]');
        if (warning) warning.remove();
      }
    }

    showSelectWarning() {
      let warning = this.stage.querySelector('[data-quiz-warning]');
      if (!warning) {
        warning = el('p', {
          class: 'quiz__error', 'data-quiz-warning': true, role: 'alert',
          text: COPY.pickToContinue,
        });
        const actions = this.stage.querySelector('.quiz__actions');
        actions.after(warning);
      }
      warning.classList.remove('quiz__helper--pulse');
      void warning.offsetWidth;
      warning.classList.add('quiz__helper--pulse');
    }

    pulseCounter() {
      const counter = this.stage.querySelector('[data-quiz-counter]');
      if (!counter) return;
      counter.classList.remove('quiz__helper--pulse');
      void counter.offsetWidth;
      counter.classList.add('quiz__helper--pulse');
    }

    radioKeydown(event, question) {
      const keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      const cards = [...this.stage.querySelectorAll('.quiz__option')];
      const current = cards.indexOf(document.activeElement);
      const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
      const target = cards[(current + delta + cards.length) % cards.length];
      target.focus();
      const option = question.options.find((o) => o.id === target.dataset.optionId);
      if (option) this.pickOption(question, option);
    }

    goBack() {
      if (this.stepIndex === 0) {
        this.showIntro();
        return;
      }
      this.stepIndex -= 1;
      this.showQuestion();
    }

    goNext() {
      const visible = this.visibleQuestions();
      if (this.stepIndex >= visible.length - 1) {
        this.goToEmail();
      } else {
        this.stepIndex += 1;
        this.showQuestion();
      }
    }

    /* ---------- submission ---------- */

    /**
     * POST the answers once per distinct answer set. Fenced: a superseded
     * request (customer went Back and changed an answer while it was in
     * flight) can still resolve, but only the current promise may commit
     * `this.submission` or emit the completion event.
     */
    ensureSubmission() {
      const current = this.fingerprint();
      if (this.submissionPromise && this.submittedFingerprint === current) return this.submissionPromise;
      this.submission = null;
      this.submittedFingerprint = current;
      const promise = this.postSubmission().then((data) => {
        if (this.submissionPromise === promise) this.submission = data;
        return data;
      });
      this.submissionPromise = promise;
      return promise;
    }

    async postSubmission() {
      const response = await this.fetchJson('/v1/submissions', {
        method: 'POST',
        body: JSON.stringify({ answers: this.answers }),
      });
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After'), 10) || 15;
        const error = new Error('rate_limited');
        error.rateLimited = true;
        error.retryAfter = retryAfter;
        throw error;
      }
      if (!response.ok) throw new Error(`submission ${response.status}`);
      return response.json();
    }

    goToEmail() {
      const promise = this.ensureSubmission();
      promise.catch(() => {}); // surfaced when the customer continues, never mid-typing
      this.setProgress(1, 1);
      this.showEmailScreen();
    }

    /** Returns the HTTP status: 204 on success, 0 on network failure. */
    async putEmail(payload) {
      if (!this.submission) return 0;
      try {
        const response = await this.fetchJson(`/v1/submissions/${this.submission.submission_id}/email`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        return response.status;
      } catch (error) {
        return 0;
      }
    }

    /**
     * Shared name/email fieldset used by the capture screen, the late form on
     * results, and the consult opt-in. Returns the fields plus validate():
     * the trimmed address, or null after showing the inline error.
     */
    emailFields({ withConsent }) {
      const firstName = el('input', {
        class: 'quiz__input', type: 'text', autocomplete: 'given-name',
        placeholder: COPY.firstNamePlaceholder, 'aria-label': COPY.firstNamePlaceholder,
      });
      const email = el('input', {
        class: 'quiz__input', type: 'email', autocomplete: 'email',
        placeholder: COPY.emailPlaceholder, 'aria-label': 'Email',
      });
      const consent = withConsent ? el('input', { class: 'quiz__checkbox', type: 'checkbox' }) : null;
      const errorLine = el('p', { class: 'quiz__error', role: 'alert', hidden: true });
      const fields = {
        firstName,
        email,
        consent,
        errorLine,
        nodes: [
          firstName,
          email,
          consent ? el('label', { class: 'quiz__consent' }, [consent, el('span', { text: COPY.consent })]) : null,
          errorLine,
        ],
        showError(message) {
          errorLine.textContent = message;
          errorLine.hidden = false;
        },
        clearError() {
          errorLine.hidden = true;
        },
        validate(required) {
          const address = email.value.trim();
          if (!address && !required) return '';
          if (!EMAIL_RE.test(address)) {
            fields.showError(COPY.emailInvalid);
            return null;
          }
          return address;
        },
        payload(address, { contactOptIn = false } = {}) {
          return {
            email: address,
            marketing_consent: consent ? consent.checked : false,
            ...(firstName.value.trim() ? { first_name: firstName.value.trim() } : {}),
            ...(contactOptIn ? { contact_opt_in: true } : {}),
          };
        },
      };
      return fields;
    }

    /** Remember what a successful PUT attached, so later PUTs resend the same consent. */
    commitEmail(payload) {
      this.email = payload.email;
      this.firstName = payload.first_name || null;
      this.marketingConsent = payload.marketing_consent;
      this.emailSkipped = false;
    }

    showEmailScreen() {
      const fields = this.emailFields({ withConsent: true });

      const skip = () => {
        this.track('email_skipped');
        this.resolveResult();
      };

      const submitButton = el('button', {
        type: 'button', class: 'button', text: COPY.seeResults,
        onclick: async () => {
          const address = fields.validate(false);
          if (address === null) return;
          if (address === '') {
            skip(); // empty email behaves as skip, per spec
            return;
          }
          fields.clearError();
          submitButton.disabled = true;
          try {
            await this.ensureSubmission();
          } catch (error) {
            this.handleSubmissionError(error);
            return;
          }
          const payload = fields.payload(address);
          const status = await this.putEmail(payload);
          if (status === 204) {
            this.commitEmail(payload);
            this.track('email_submitted', { consent: payload.marketing_consent });
            this.showResult();
          } else {
            submitButton.disabled = false;
            fields.showError(status === 422 ? COPY.emailInvalid : COPY.emailPutFailed);
          }
        },
      });

      const body = el('div', { class: 'quiz__question v-stack gap-4' }, [
        el('h2', { class: 'quiz__prompt h2', 'data-quiz-focus': true, text: COPY.emailHeadline }),
        el('p', { class: 'quiz__helper', text: COPY.emailSub }),
        el('div', { class: 'quiz__form v-stack gap-2' }, fields.nodes),
        el('div', { class: 'quiz__actions v-stack gap-2' }, [
          submitButton,
          el('button', { type: 'button', class: 'quiz__text-link', text: COPY.skip, onclick: skip }),
        ]),
      ]);
      this.swapStage(body);
    }

    /** Wait out the background POST (skip/retry paths), then render the result. */
    async resolveResult() {
      if (!this.submission) {
        this.swapStage(el('div', { class: 'quiz__question v-stack gap-4' }, [
          el('h2', { class: 'quiz__prompt h2', 'data-quiz-focus': true, text: COPY.scoring }),
          el('div', { class: 'quiz__spinner', 'aria-hidden': 'true' }),
        ]));
        try {
          await this.ensureSubmission();
        } catch (error) {
          this.handleSubmissionError(error);
          return;
        }
      }
      this.showResult();
    }

    handleSubmissionError(error) {
      if (error && error.rateLimited) this.showRateLimited(error.retryAfter);
      else this.showSubmitError();
    }

    showSubmitError() {
      this.swapStage(el('div', { class: 'quiz__question v-stack gap-4' }, [
        el('h2', { class: 'quiz__prompt h2', 'data-quiz-focus': true, text: COPY.submitFailed }),
        el('div', { class: 'quiz__actions' }, [
          el('button', { type: 'button', class: 'button', text: COPY.retry, onclick: () => {
            // Safe: a failed POST left no submission_id behind.
            this.resetSubmission();
            this.resolveResult();
          } }),
        ]),
      ]));
    }

    showRateLimited(retryAfter) {
      const button = el('button', { type: 'button', class: 'button', disabled: true, text: COPY.retryIn(retryAfter) });
      this.swapStage(el('div', { class: 'quiz__question v-stack gap-4' }, [
        el('h2', { class: 'quiz__prompt h2', 'data-quiz-focus': true, text: COPY.rateLimited }),
        el('div', { class: 'quiz__actions' }, [button]),
      ]));
      let remaining = retryAfter;
      this.rateLimitTimer = setInterval(() => {
        remaining -= 1;
        if (remaining > 0) {
          button.textContent = COPY.retryIn(remaining);
        } else {
          clearInterval(this.rateLimitTimer);
          this.rateLimitTimer = null;
          this.resetSubmission();
          this.resolveResult();
        }
      }, 1000);
    }

    /* ---------- results ---------- */

    showResult() {
      this.progress.hidden = true;
      const { tier, profile, result, submission_id: submissionId } = this.submission;
      /* Emitted here, not on POST resolve: a submission abandoned via Back
         (its result never rendered) must not count as a completion. */
      if (this.completedSubmissionId !== submissionId) {
        this.completedSubmissionId = submissionId;
        this.track('complete', { tier, profile, submission_id: submissionId });
      }
      const body = el('div', { class: 'quiz__result v-stack gap-5' }, [
        // Eyebrow + headline are a tight pair, so they get their own small gap
        // rather than the larger gap-5 between the result's major blocks.
        el('div', { class: 'v-stack gap-2' }, [
          el('span', { class: 'quiz__eyebrow', text: COPY.resultEyebrow }),
          el('h2', { class: 'quiz__headline', 'data-quiz-focus': true, text: result.headline }),
        ]),
        el('p', { class: 'quiz__summary', text: result.summary }),
        ...result.notes.map((note) => el('p', { class: 'quiz__note', text: note })),
      ]);

      if (tier === 'high') {
        body.append(this.chipRow(result), this.routineList(result.routine));
        if (this.emailSkipped) body.append(this.lateEmailForm(COPY.emailMeRoutine));
      } else if (tier === 'medium') {
        body.append(this.routineList(result.routine), this.retakeBand());
      } else {
        body.append(this.consultCard());
      }

      body.append(el('div', { class: 'quiz__result-footer' }, [
        el('button', { type: 'button', class: 'quiz__text-link', text: COPY.retakeLink, onclick: () => this.retake() }),
      ]));
      this.swapStage(body);
    }

    chipRow(result) {
      const groups = [];
      if (result.skin_type) {
        groups.push(this.chipGroup(COPY.skinTypeLabel, [
          el('span', { class: 'quiz__chip quiz__chip--type', text: result.skin_type.label }),
        ]));
      }
      if (result.concerns.length) {
        groups.push(this.chipGroup(COPY.concernsLabel,
          result.concerns.map((concern) => el('span', { class: 'quiz__chip', text: concern.label }))));
      }
      return el('div', { class: 'quiz__chip-groups v-stack gap-3' }, groups);
    }

    chipGroup(label, chips) {
      return el('div', { class: 'quiz__chip-group v-stack gap-1' }, [
        el('span', { class: 'quiz__chip-label', text: label }),
        el('div', { class: 'quiz__chips' }, chips),
      ]);
    }

    routineList(routine) {
      if (!routine.length) return null;
      const items = routine.map((step, index) => el('li', { class: 'quiz__slot' }, [
        el('span', { class: 'quiz__slot-number', 'aria-hidden': 'true', text: String(index + 1) }),
        el('div', { class: 'quiz__slot-body v-stack gap-1' }, [
          el('div', { class: 'quiz__slot-head' }, [
            el('h3', { class: 'quiz__slot-name', text: slotLabel(step.slot) }),
            step.usage ? el('span', { class: 'quiz__tag', text: step.usage }) : null,
            step.optional ? el('span', { class: 'quiz__tag quiz__tag--optional', text: COPY.optionalTag }) : null,
          ]),
          el('p', { class: 'quiz__slot-descriptor', text: step.descriptor }),
          // A recommended product per slot when the backend has one; an empty
          // products[] (nothing suitable in stock) falls back to descriptor only.
          this.slotProducts(step),
        ]),
      ]));
      const block = el('div', { class: 'v-stack gap-3' }, [
        el('h3', { class: 'quiz__subhead', text: COPY.yourRoutine }),
        el('ol', { class: 'quiz__routine v-stack gap-2' }, items),
      ]);
      // "Add all to cart" + "View cart" — present only when the backend has
      // supplied at least one variant_id (add-to-cart cannot work without it).
      const cartActions = this.routineCartActions(routine);
      if (cartActions) block.append(cartActions);
      return block;
    }

    slotProducts(step) {
      const products = step.products || [];
      if (!products.length) return null;
      return el('div', { class: 'quiz__slot-products v-stack gap-2' },
        products.map((product) => this.productCard(step, product)));
    }

    productCard(step, product) {
      // Whole-card link split into image + title links: an "Add to cart" button
      // can't live inside an <a>, so the card is a container with sibling links.
      const trackClick = () => this.track('product_click', { slot: step.slot, sku: product.sku });
      const media = product.image_url
        ? el('a', {
            class: 'quiz__product-media', href: product.url,
            tabindex: '-1', 'aria-hidden': 'true', onclick: trackClick,
          }, [
            el('img', {
              class: 'quiz__product-img', src: product.image_url, alt: '',
              loading: 'lazy', decoding: 'async',
            }),
          ])
        : null;
      const hasPrice = product.price != null && product.price !== '';
      return el('div', { class: 'quiz__product' }, [
        media,
        el('div', { class: 'quiz__product-body v-stack gap-1' }, [
          product.vendor ? el('span', { class: 'quiz__product-vendor', text: product.vendor }) : null,
          el('a', { class: 'quiz__product-title', href: product.url, text: product.title, onclick: trackClick }),
          hasPrice ? el('span', { class: 'quiz__product-price', text: formatPrice(product.price) }) : null,
          // Add-to-cart appears only once the backend supplies the exact variant id.
          product.variant_id ? this.addToCartControl(step, product) : null,
        ]),
      ]);
    }

    /* ---------- cart ---------- */

    shopifyRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    cartUrl() {
      return `${this.shopifyRoot()}cart`;
    }

    /** POST to /cart/add.js; single item as {id, quantity}, many as {items}. */
    async cartAdd(items) {
      const body = items.length === 1
        ? { id: items[0].id, quantity: items[0].quantity }
        : { items };
      const response = await fetch(`${this.shopifyRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      return response.ok;
    }

    addToCartControl(step, product) {
      const button = el('button', {
        type: 'button', class: 'quiz__product-add', text: COPY.addToCart,
        onclick: () => this.addToCart(step, product, button),
      });
      return button;
    }

    async addToCart(step, product, button) {
      const key = String(product.variant_id);
      // Already in cart, or reserved by an in-flight "Add all": adding again would duplicate.
      if (this.cartAdded.has(key)) return;
      this.clearInlineError(button);
      const restore = button.textContent;
      button.disabled = true;
      this.cartAdded.add(key); // reserve before the await so a racing "Add all" skips it
      try {
        if (!(await this.cartAdd([{ id: product.variant_id, quantity: 1 }]))) throw new Error('cart add failed');
        button.textContent = COPY.inCart;
        button.classList.add('quiz__product-add--done');
        this.track('add_to_cart', { slot: step.slot, sku: product.sku, variant_id: product.variant_id });
        this.revealViewCart();
      } catch (error) {
        this.cartAdded.delete(key); // release the reservation so it can be retried
        button.disabled = false;
        button.textContent = restore;
        this.showInlineError(button);
      }
    }

    routineCartActions(routine) {
      const items = [];
      for (const step of routine) {
        for (const product of step.products || []) {
          if (product.variant_id) items.push({ id: product.variant_id, quantity: 1 });
        }
      }
      if (!items.length) return null;
      const viewCart = el('a', {
        class: 'quiz__text-link quiz__view-cart', href: this.cartUrl(),
        text: COPY.viewCart, 'data-quiz-view-cart': true, hidden: true,
      });
      const addAll = el('button', {
        type: 'button', class: 'button quiz__add-all', text: COPY.addAllToCart,
        onclick: () => this.addAllToCart(items, addAll),
      });
      return el('div', { class: 'quiz__cart-actions v-stack gap-2' }, [addAll, viewCart]);
    }

    async addAllToCart(items, button) {
      this.clearInlineError(button);
      // Skip anything already added per-card so "Add all" never duplicates a line.
      const pending = items.filter((item) => !this.cartAdded.has(String(item.id)));
      if (!pending.length) {
        window.location.href = this.cartUrl();
        return;
      }
      button.disabled = true;
      pending.forEach((item) => this.cartAdded.add(String(item.id))); // reserve before the await
      try {
        if (!(await this.cartAdd(pending))) throw new Error('cart add-all failed');
        this.track('add_to_cart', { all: true, count: pending.length });
        window.location.href = this.cartUrl();
      } catch (error) {
        pending.forEach((item) => this.cartAdded.delete(String(item.id))); // release on failure
        button.disabled = false;
        this.showInlineError(button);
      }
    }

    revealViewCart() {
      const link = this.stage.querySelector('[data-quiz-view-cart]');
      if (link) link.hidden = false;
    }

    /** Inline .quiz__error rendered as a direct sibling of the failing control. */
    showInlineError(afterNode, message = COPY.addToCartFailed) {
      let error = afterNode.parentElement.querySelector(':scope > [data-quiz-add-error]');
      if (!error) {
        error = el('p', { class: 'quiz__error', role: 'alert', 'data-quiz-add-error': true });
        afterNode.after(error);
      }
      error.textContent = message;
    }

    clearInlineError(afterNode) {
      const error = afterNode.parentElement.querySelector(':scope > [data-quiz-add-error]');
      if (error) error.remove();
    }

    retakeBand() {
      const band = el('div', { class: 'quiz__retake v-stack gap-2' }, [
        el('h3', { class: 'quiz__subhead', text: COPY.retakeHeadline }),
        el('p', { class: 'quiz__helper', text: COPY.retakeWhy }),
      ]);
      if (this.emailSkipped) band.append(this.lateEmailForm(COPY.retakeRemind, 'button--secondary', COPY.retakeReminderSet));
      else band.append(el('p', { class: 'quiz__hint', text: COPY.retakeReminderSet }));
      return band;
    }

    lateEmailForm(ctaLabel, buttonClass = '', savedCopy = COPY.emailSaved) {
      const fields = this.emailFields({ withConsent: true });
      const button = el('button', {
        type: 'button', class: `button ${buttonClass}`.trim(), text: ctaLabel,
        onclick: async () => {
          const address = fields.validate(true);
          if (address === null) return;
          fields.clearError();
          button.disabled = true;
          const payload = fields.payload(address);
          const status = await this.putEmail(payload);
          if (status === 204) {
            this.commitEmail(payload);
            this.track('email_submitted', { consent: payload.marketing_consent, late: true });
            wrap.replaceChildren(el('p', { class: 'quiz__hint', text: savedCopy }));
          } else {
            button.disabled = false;
            fields.showError(status === 422 ? COPY.emailInvalid : COPY.emailPutFailed);
          }
        },
      });
      const wrap = el('div', { class: 'quiz__late-email v-stack gap-2' }, [...fields.nodes, button]);
      return wrap;
    }

    /* ---------- consult tier ---------- */

    consultCard() {
      const card = el('div', { class: 'quiz__consult v-stack gap-3' }, [
        el('p', { class: 'quiz__consult-ask', text: COPY.consultAsk }),
        el('div', { class: 'quiz__actions v-stack gap-2' }, [
          el('button', { type: 'button', class: 'button', text: COPY.consultYes, onclick: (event) => this.consultYes(card, event.target) }),
          el('button', { type: 'button', class: 'button button--secondary', text: COPY.consultNo, onclick: () => {
            this.track('consult_declined');
            card.replaceChildren(
              el('p', { class: 'quiz__helper', text: COPY.consultDeclined }),
              el('a', { class: 'button button--secondary', href: this.browseUrl, text: this.browseLabel }),
            );
          } }),
        ]),
      ]);
      return card;
    }

    async consultYes(card, yesButton) {
      if (this.email) {
        // Re-PUT the stored email/consent unchanged; only contact_opt_in is new.
        yesButton.disabled = true;
        const status = await this.putEmail({
          email: this.email,
          marketing_consent: this.marketingConsent,
          ...(this.firstName ? { first_name: this.firstName } : {}),
          contact_opt_in: true,
        });
        if (status === 204) {
          this.track('consult_optin');
          card.replaceChildren(el('p', { class: 'quiz__consult-confirm', text: COPY.consultConfirmed }));
        } else {
          yesButton.disabled = false;
          const previous = card.querySelector('.quiz__error');
          if (previous) previous.remove();
          card.prepend(el('p', { class: 'quiz__error', role: 'alert', text: COPY.emailPutFailed }));
        }
        return;
      }

      // Consult contact is a one-off service reply, not marketing: no consent
      // checkbox here, marketing_consent stays false.
      const fields = this.emailFields({ withConsent: false });
      const button = el('button', {
        type: 'button', class: 'button', text: COPY.consultYes,
        onclick: async () => {
          const address = fields.validate(true);
          if (address === null) return;
          fields.clearError();
          button.disabled = true;
          const payload = fields.payload(address, { contactOptIn: true });
          const status = await this.putEmail(payload);
          if (status === 204) {
            this.commitEmail(payload);
            this.track('consult_optin');
            card.replaceChildren(el('p', { class: 'quiz__consult-confirm', text: COPY.consultConfirmed }));
          } else {
            button.disabled = false;
            fields.showError(status === 422 ? COPY.emailInvalid : COPY.emailPutFailed);
          }
        },
      });
      card.replaceChildren(
        el('p', { class: 'quiz__helper', text: COPY.consultEmailLead }),
        ...fields.nodes,
        button,
      );
    }

    /* ---------- retake ---------- */

    retake() {
      this.track('retake');
      if (this.rateLimitTimer) clearInterval(this.rateLimitTimer);
      this.initState();
      this.showIntro();
    }
  }

  if (!customElements.get('skin-quiz')) customElements.define('skin-quiz', SkinQuiz);
})();

/* ---------- GA4 forwarding ---------- */

(() => {
  const EVENTS = [
    'start',
    'question_view',
    'question_answer',
    'email_submitted',
    'email_skipped',
    'complete',
    'product_click',
    'add_to_cart',
    'consult_optin',
    'consult_declined',
    'retake',
  ];

  // GA4 is loaded into the main window by the Google & YouTube channel web pixel
  // (OPEN runtime), which drains this same queue. Pushing arguments-objects works
  // whether gtag.js is already present, loads later, or is blocked.
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }

  // GA4 params must be scalar: colons are invalid in event names, arrays are joined,
  // nested objects are dropped.
  const toParams = (detail = {}) => {
    const params = {};
    for (const [key, value] of Object.entries(detail)) {
      if (Array.isArray(value)) params[key] = value.join(',');
      else if (value !== null && typeof value === 'object') continue;
      else params[key] = value;
    }
    return params;
  };

  for (const name of EVENTS) {
    document.addEventListener(`quiz:${name}`, (event) => {
      // A quiz page must never break because of analytics.
      try {
        gtag('event', `quiz_${name}`, toParams(event.detail));
      } catch (_) { /* swallow */ }
    });
  }
})();
